import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplets, Gauge, Leaf, ThermometerSun, Users, Zap } from 'lucide-react';
import { MapViewport, type MapMarker } from '../components/MapViewport';
import { globalEventLayer } from '../map/data/demoLayers';
import { Panel, Pill, StatusRow } from '../components/ui';
import type { WorldViewportHandle } from '../map/WorldViewport';

const markers: MapMarker[] = [
  { id: 'north-america', longitude: -120.4, latitude: 55.8, tone: 'red', pulse: true, label: 'North America' },
  { id: 'caribbean', longitude: -70, latitude: 20, tone: 'purple', label: 'Caribbean' },
  { id: 'south-america', longitude: -52.2, latitude: -12.8, tone: 'green', label: 'Brazil' },
  { id: 'europe', longitude: 31.2, latitude: 49.1, tone: 'red', pulse: true, label: 'Europe' },
  { id: 'africa', longitude: 20, latitude: 15, tone: 'orange', label: 'Sahel' },
  { id: 'west-africa', longitude: -3, latitude: 8, tone: 'cyan', label: 'West Africa' },
];

const hotspots = [
  { title: 'Ukraine Conflict', subtitle: 'High impact', tone: 'red', image: '/assets/maps/crisis.webp', longitude: 31.2, latitude: 49.1 },
  { title: 'Gaza Crisis', subtitle: 'Critical humanitarian', tone: 'red', image: '/assets/maps/crisis.webp', longitude: 34.47, latitude: 31.5 },
  { title: 'Iran Tensions', subtitle: 'High risk', tone: 'orange', image: '/assets/maps/earth.webp', longitude: 53.7, latitude: 32.4 },
  { title: 'Cyclone Chido', subtitle: 'Mozambique Channel', tone: 'blue', image: '/assets/maps/forecast.webp', longitude: 42.2, latitude: -15.2 },
  { title: 'Wildfire', subtitle: 'Canada', tone: 'orange', image: '/assets/maps/events.webp', longitude: -120.4, latitude: 55.8 },
  { title: 'Flooding', subtitle: 'Brazil', tone: 'yellow', image: '/assets/maps/infrastructure.webp', longitude: -52.2, latitude: -12.8 },
] as const;

export function OverviewPage() {
  const navigate = useNavigate();
  const mapController = useRef<WorldViewportHandle | null>(null);
  const [selected, setSelected] = useState('Cyclone Chido');
  return (
    <div className="overview-layout page-grid">
      <div className="overview-globe">
        <MapViewport controllerRef={mapController} markers={markers} layers={[globalEventLayer]} initialCamera={{ longitude: -8, latitude: 18, height: 18_000_000, pitch: -90 }} onMarkerClick={(marker) => setSelected(marker.label ?? marker.id)}>
          <div className="map-caption"><span className="live-dot" /> Live global snapshot · selected: {selected}</div>
        </MapViewport>
      </div>
      <div className="overview-rail">
        <Panel title={<span className="eyebrow">Planet status</span>} action={<Pill tone="green">● Live</Pill>}>
          <div className="status-list">
            <StatusRow icon={<Users size={16} />} label="Population" value="8,102,344,514" detail="+0.55%" tone="purple" />
            <StatusRow icon={<Gauge size={16} />} label="CO₂ level" value="421.3 ppm" detail="+1.2%" tone="orange" />
            <StatusRow icon={<ThermometerSun size={16} />} label="Global temp" value="+1.32 °C" detail="+0.05 °C" tone="orange" />
            <StatusRow icon={<Zap size={16} />} label="Energy demand" value="18,760 GW" detail="+2.1%" tone="cyan" />
            <StatusRow icon={<Droplets size={16} />} label="Water stress" value="34%" detail="Medium" tone="yellow" />
          </div>
        </Panel>
        <Panel title={<span className="eyebrow">Active alerts</span>}>
          <div className="alert-summary">
            <div><strong className="text-red">12</strong><span>Critical</span></div>
            <div><strong className="text-yellow">48</strong><span>Warning</span></div>
            <div><strong className="text-blue">128</strong><span>Info</span></div>
          </div>
        </Panel>
        <Panel className="overview-rail__eco" title={<span className="eyebrow">Planetary signal</span>}>
          <div className="eco-card"><Leaf size={21} /><div><strong>98.6%</strong><span>Data quality score</span></div></div>
        </Panel>
      </div>
      <Panel className="hotspot-strip" title={<span className="eyebrow">Hotspots</span>} action={<button type="button" className="text-button" onClick={() => navigate('/events')}>View all</button>}>
        <div className="hotspot-grid">
          {hotspots.map((hotspot) => (
            <button key={hotspot.title} type="button" className={`hotspot-card ${selected === hotspot.title ? 'is-selected' : ''}`} onClick={() => { setSelected(hotspot.title); void mapController.current?.flyTo([hotspot.longitude, hotspot.latitude, 4_800_000], { durationMs: 850 }); }}>
              <div className="hotspot-card__image" style={{ backgroundImage: `url(${hotspot.image})` }} />
              <strong>{hotspot.title}</strong>
              <span><i className={`severity-dot severity-dot--${hotspot.tone}`} />{hotspot.subtitle}</span>
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
}
